<template>
  <Transition name="fade">
    <DrawerDialog
      v-if="show"
      title="Change order type"
      :show-cancel-button="false"
      :show-save-button="false"
      :custom-footer="true"
      :show-footer="true"
      :show-close-button="true"
      :sm="true"
      @closeEvent="close"
    >
      <template #modal-content>
        <div class="flex flex-col gap-4 text-sm">
          <p class="text-xs text-ink-mute">
            Reclassifying an order is its own decision, so it is recorded on its own: the
            reason goes to the order's audit trail and comment feed, and a type that counts as
            a failure opens the remake intake questionnaire.
          </p>

          <div class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-ink-soft">
              Order type <span class="text-red-500">*</span>
            </label>
            <Select
              append-to-body
              v-model="selectedType"
              :options="orderTypes"
              label="label"
              placeholder="Select order type"
              :clearable="false"
              class="w-full text-sm"
              @option:selected="onTypeSelected"
            />
            <p v-if="currentSymbol" class="text-xs text-ink-mute">
              Currently <span class="font-medium">{{ currentSymbol }}</span>.
            </p>
          </div>

          <!--
            Hidden while the order is being classified as a failure: the questionnaire
            records what went wrong in full, and 'failure_identified' is the only reason that
            could honestly sit beside it. Still sent, so every type change carries the same
            audit field.
          -->
          <div v-if="typeHasChanged && !reasonIsAutomatic" class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-ink-soft">
              Reason for change <span class="text-red-500">*</span>
            </label>
            <Select
              append-to-body
              v-model="reasonCode"
              :options="availableReasons"
              label="label"
              :reduce="(reason) => reason.code"
              placeholder="Why is the order type changing?"
              :clearable="false"
              class="w-full text-sm"
            />
          </div>

          <div v-if="typeHasChanged && requiresOriginalOrder" class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-ink-soft">
              Original order number <span class="text-red-500">*</span>
            </label>
            <input
              v-model="originalOrderNo"
              type="text"
              inputmode="numeric"
              placeholder="e.g. 26171"
              class="w-full rounded-md border border-line px-3 py-2 text-sm focus:outline-none"
            />
            <p class="text-xs text-ink-mute">
              The order this one replaces, so it traces back to its Order Administrator.
            </p>
          </div>

          <div v-if="typeHasChanged" class="flex flex-col gap-1.5">
            <label class="text-sm font-medium text-ink-soft">
              Comment
              <span v-if="commentRequired" class="text-red-500">*</span>
              <span v-else class="text-ink-mute">(optional)</span>
            </label>
            <textarea
              v-model="comment"
              rows="3"
              :placeholder="commentRequired
                ? 'Required for &quot;Other&quot; — describe the change in your own words'
                : 'Anything the reason above does not already say'"
              class="w-full rounded-md border border-line px-3 py-2 text-sm focus:outline-none"
            ></textarea>
          </div>

          <!--
            The questionnaire is a separate step rather than inlined here, so it is obvious
            that a failure record is being created and not merely a field edited.
          -->
          <div
            v-if="typeHasChanged && requiresFailureRecord"
            class="flex items-center justify-between gap-3 rounded-md border p-3"
            :class="failureRecordAnswers ? 'border-[var(--accent)] bg-primary/5' : 'border-amber-200 bg-amber-50'"
          >
            <p class="text-xs" :class="failureRecordAnswers ? 'text-ink-soft' : 'text-amber-900'">
              <template v-if="failureRecordAnswers">
                Questionnaire completed. Saving will record it against this order.
              </template>
              <template v-else>
                <strong>This type counts as a failure.</strong>
                The remake intake questionnaire is required before saving.
              </template>
            </p>
            <button
              class="whitespace-nowrap rounded-md border border-[var(--accent)] px-2 py-1.5 text-xs font-medium text-[var(--accent)]"
              @click="showQuestionnaire = true"
            >
              {{ failureRecordAnswers ? 'Review answers' : 'Open questionnaire' }}
            </button>
          </div>
        </div>
      </template>

      <template #footer>
        <div class="flex w-full items-center justify-between gap-3">
          <p v-if="validationError" class="text-xs text-red-500">{{ validationError }}</p>
          <span v-else></span>
          <div class="flex gap-2">
            <button class="rounded-md bg-[#a0a0a0] px-3 py-2 text-ink-soft" @click="close">
              Cancel
            </button>
            <button
              class="rounded-md bg-[var(--accent)] px-3 py-2 text-white disabled:cursor-not-allowed disabled:opacity-50"
              :disabled="Boolean(validationError) || saving"
              @click="save"
            >
              {{ saving ? 'Saving…' : 'Save change' }}
            </button>
          </div>
        </div>
      </template>
    </DrawerDialog>
  </Transition>

  <RemakeIntakeQuestionnaireModal
    :show="showQuestionnaire"
    :order-no="orderNo"
    :original-order-no="originalOrderNo || null"
    :suggested-record-type="suggestedRecordType"
    :order-type-symbol="selectedType?.symbol ?? null"
    @submit="onQuestionnaireSubmit"
    @cancel="showQuestionnaire = false"
  />
</template>

<script setup lang="ts">
/**
 * AB#20569 / AB#20571 — changing an order's type as its own action.
 *
 * WHY THIS IS NOT IN THE PRICE MODAL
 * Order type used to be editable only inside the price breakdown, which meant reclassifying
 * an order and repricing it were one save, and the remake intake questionnaire — a form
 * about failure cause and responsibility — opened from inside a pricing dialog. AB#20571
 * asked for the two to be separated; this is the front-end half of that.
 *
 * The price modal still accepts a type change for now. Removing it changes an existing admin
 * workflow, so it is its own piece of work rather than a side effect of this one.
 *
 * Hidden behind can('change-order-type') by the caller. The button being hidden is never the
 * only protection: the route carries routeHasPermission and orderTypeChangeAccess, and the
 * service refuses a role that config('custimoo.order_type_change') does not admit.
 */
import { computed, ref, watch } from 'vue';
import DrawerDialog from '@/components/Shared/DrawerDialog.vue';
import Select from '@/components/Shared/Select.vue';
import RemakeIntakeQuestionnaireModal from '@/components/Dashboard/Modals/RemakeIntakeQuestionnaireModal.vue';
import { handleResponseException, http } from '@/interceptors/axiosRequestInterceptor';
import { showToast } from '@/composables/swal';
import type { OrderType } from '@/models';
import {
  findOrderType,
  orderTypeCountsAsFailure,
  orderTypeRequiresOriginalOrder,
  orderTypes,
} from '@/utils/constants/orderTypes';
import {
  ORDER_TYPE_CHANGE_COMMENT_MIN_LENGTH,
  findOrderTypeChangeReason,
  reasonsForOrderType,
} from '@/utils/constants/orderTypeChangeReasons';

const props = defineProps<{
  show: boolean;
  /** Public order number, or the internal id when the order has no number yet. */
  orderNo: string | number;
  /** The order's current symbol, so "has it changed" is answerable. */
  currentSymbol?: string | null;
}>();

const emit = defineEmits<{
  (e: 'close'): void;
  /** Emitted after a successful save so the parent can refresh the order. */
  (e: 'saved'): void;
}>();

const selectedType = ref<OrderType | null>(null);
const reasonCode = ref<string | null>(null);
const comment = ref('');
const originalOrderNo = ref('');
const showQuestionnaire = ref(false);
const failureRecordAnswers = ref<Record<string, unknown> | null>(null);
const saving = ref(false);

// Reset on each open. A cancelled change must never leak into the next one.
watch(
  () => props.show,
  (open) => {
    if (!open) return;
    selectedType.value = findOrderType(props.currentSymbol) ?? null;
    reasonCode.value = null;
    comment.value = '';
    originalOrderNo.value = '';
    failureRecordAnswers.value = null;
    showQuestionnaire.value = false;
    saving.value = false;
  },
);

const typeHasChanged = computed(
  () => Boolean(selectedType.value?.symbol) && selectedType.value?.symbol !== props.currentSymbol,
);

const availableReasons = computed(() => reasonsForOrderType(selectedType.value?.symbol));

/** The questionnaire owns the explanation, so the reason is not asked — only sent. */
const reasonIsAutomatic = computed(
  () => typeHasChanged.value && requiresFailureRecord.value,
);

const AUTOMATIC_FAILURE_REASON = 'failure_identified';

/** What actually goes on the wire. */
const effectiveReasonCode = computed(
  () => (reasonIsAutomatic.value ? AUTOMATIC_FAILURE_REASON : reasonCode.value),
);

const requiresOriginalOrder = computed(() =>
  orderTypeRequiresOriginalOrder(selectedType.value?.symbol),
);

const requiresFailureRecord = computed(() =>
  orderTypeCountsAsFailure(selectedType.value?.symbol),
);

const commentRequired = computed(
  () => findOrderTypeChangeReason(effectiveReasonCode.value)?.requiresComment === true,
);

const suggestedRecordType = computed<string | null>(() => {
  const symbol = selectedType.value?.symbol;
  if (symbol === 'Ri' || symbol === 'R') return 'remake';
  if (symbol === 'REORDER') return 'reorder';
  return null;
});

/**
 * Keep the reason honest as the target type moves, and preselect the one true answer when
 * the order is being classified as a failure.
 *
 * Classifying a remake used to leave the admin picking "New information" or "Other" after
 * completing the whole questionnaire — a required field with no truthful option in it.
 */
watch(selectedType, () => {
  if (reasonCode.value && !availableReasons.value.some((r) => r.code === reasonCode.value)) {
    reasonCode.value = null;
  }

  // Mandatory, so it is opened rather than advertised. Leaving it behind a button meant the
  // admin filled everything else and only met the requirement when the save refused.
  if (requiresFailureRecord.value && !failureRecordAnswers.value) {
    showQuestionnaire.value = true;
  }
});

/** Why the save is blocked, as a message rather than a boolean, so it can be shown. */
const validationError = computed<string | null>(() => {
  if (!selectedType.value) return 'Select an order type.';
  if (!typeHasChanged.value) return 'This is already the order type.';
  if (!effectiveReasonCode.value) return 'Select a reason for the change.';
  if (requiresOriginalOrder.value && !originalOrderNo.value.trim()) {
    return 'A remake or reorder needs the original order number.';
  }
  if (commentRequired.value && comment.value.trim().length < ORDER_TYPE_CHANGE_COMMENT_MIN_LENGTH) {
    return `The reason you chose needs a comment of at least ${ORDER_TYPE_CHANGE_COMMENT_MIN_LENGTH} characters.`;
  }
  // The API's rule is min:20 on the field itself, not only when it is required, so a short
  // optional note would come back as a 422. Said here instead, while it can still be fixed.
  const typed = comment.value.trim().length;
  if (typed > 0 && typed < ORDER_TYPE_CHANGE_COMMENT_MIN_LENGTH) {
    return `A comment must be at least ${ORDER_TYPE_CHANGE_COMMENT_MIN_LENGTH} characters, or left empty.`;
  }
  if (requiresFailureRecord.value && !failureRecordAnswers.value) {
    return 'Complete the remake intake questionnaire before saving.';
  }
  return null;
});

function onTypeSelected(): void {
  // The reason list depends on the type, so a reason chosen for the previous one may no
  // longer apply. Clearing beats sending something the backend will refuse.
  if (reasonCode.value && !availableReasons.value.some((r) => r.code === reasonCode.value)) {
    reasonCode.value = null;
  }
  if (!requiresOriginalOrder.value) originalOrderNo.value = '';

  // A previous type's answers do not describe this one.
  failureRecordAnswers.value = null;
}

function onQuestionnaireSubmit(answers: Record<string, unknown>): void {
  failureRecordAnswers.value = answers;
  showQuestionnaire.value = false;

  // The questionnaire validates its original order against a real order, so its answer is
  // the authoritative one — mirror it back rather than letting the two fields disagree.
  const original = answers.original_order_id;
  if (typeof original === 'string' && original) originalOrderNo.value = original;
}

function close(): void {
  emit('close');
}

async function save(): Promise<void> {
  if (validationError.value || saving.value) return;

  saving.value = true;

  try {
    await http.patch(`order/${props.orderNo}/order-type`, {
      order_type_symbol: selectedType.value?.symbol,
      reason_code: effectiveReasonCode.value,
      comment: comment.value.trim() || null,
      // Only when the target type actually takes one. The field is hidden for a New order
      // but the ref keeps whatever was loaded, and sending it would ask the API to re-link
      // the order on the same save that is supposed to unlink it.
      original_order_no: requiresOriginalOrder.value ? originalOrderNo.value.trim() || null : null,
      // Sent in the same request as the type change, so neither can land without the other.
      ...(failureRecordAnswers.value ? { failure_record: failureRecordAnswers.value } : {}),
    });

    showToast('Order type saved.', 'success');
    emit('saved');
    close();
  } catch (e) {
    // The API answers 403 / 404 / 409 / 422 with a real status and a message naming the
    // field, so the interceptor's message is more useful than anything invented here.
    handleResponseException(e as never);
  } finally {
    saving.value = false;
  }
}
</script>
